import { useState } from 'react'
import { Button, ErrorBanner, Field, TextInput } from '../../components/forms'
import { formatMoneyDocument } from '../../lib/format'
import { numberOrNull } from '../../lib/forms'
import { useCloseDay, useOpenDay, useShopDay, type DayBookLine, type ShopDay } from '../../features/pos/api'

/**
 * The shop's day: what its money was at each end of it (D-195).
 *
 * **Yesterday's figures are not shown before the count.** A cashier told last night's closing cash
 * types it instead of counting, and the count is the only thing that would catch a short float. The
 * server withholds them; this only has them to show once the day is open, or for somebody who may
 * manage the day book and is the person asked to explain a difference.
 *
 * **Nothing here posts to the ledger.** A declared balance is an observation. If the till really is
 * short, that is a journal somebody makes deliberately with a reason on it.
 */
export function ShopDayPanel({ locationId }: { locationId: string }) {
  const day = useShopDay(locationId)

  if (day.isLoading || !day.data) {
    return null
  }

  return <DayBook day={day.data} locationId={locationId} />
}

function DayBook({ day, locationId }: { day: ShopDay; locationId: string }) {
  const open = useOpenDay(locationId)
  const close = useCloseDay(locationId)

  const [counted, setCounted] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')

  const phase = day.status === 'NotOpened' ? 'open' : 'close'
  const busy = open.isPending || close.isPending

  // Once the day is open both halves are shown: what it opened with, read-only, above what is being
  // recorded now. Showing only the closing table hid the morning's count the moment it was taken,
  // which is the one figure the evening's is measured against.
  const showOpening = day.status !== 'NotOpened'

  // Only what the person actually typed. An account left blank is one nobody has looked at, which
  // the server stores as "not looked at" rather than as zero.
  const declared = (): Record<string, number> =>
    Object.fromEntries(
      Object.entries(counted)
        .map(([id, text]) => [id, numberOrNull(text)] as const)
        .filter(([, value]) => value !== null && value >= 0) as [string, number][],
    )

  const yesterdayFor = (accountId: string) => day.yesterday.find((l) => l.accountId === accountId)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const body = { declared: declared(), note: note.trim() || undefined }

    if (phase === 'open') {
      await open.mutateAsync(body)
    } else {
      await close.mutateAsync({ ...body, finish: true })
    }

    setCounted({})
    setNote('')
  }

  return (
    <section className="rounded-lg border border-dpl-border bg-dpl-surface p-4">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">
          {day.status === 'NotOpened' && 'Open the day'}
          {day.status === 'Open' && "Today's balances"}
          {day.status === 'Closed' && 'The day is closed'}
        </h2>

        <p className="text-xs text-dpl-muted">
          {day.status === 'Open' && day.openedBy && `Opened by ${day.openedBy}`}
          {day.status === 'Closed' && day.closedBy && `Closed by ${day.closedBy}`}
        </p>
      </header>

      {showOpening && (
        <div className="mb-4">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-dpl-muted">Opened with</h3>
          <Balances lines={day.opening} yesterdayFor={yesterdayFor} readOnly />
        </div>
      )}

      {!day.mayManage ? (
        // A cashier sees where the shop stands and is not asked to answer for the bank.
        showOpening ? null : <Balances lines={day.opening} yesterdayFor={yesterdayFor} readOnly />
      ) : (
        <form onSubmit={submit}>
          <ErrorBanner error={open.error ?? close.error} />

          {showOpening && (
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-dpl-muted">
              {day.status === 'Closed' ? 'Closed with' : 'Closing now'}
            </h3>
          )}

          <Balances
            lines={phase === 'open' ? day.opening : day.closing}
            yesterdayFor={yesterdayFor}
            readOnly={day.status === 'Closed'}
            counted={counted}
            onCount={(id, text) => setCounted((c) => ({ ...c, [id]: text }))}
          />

          {day.status !== 'Closed' && (
            <>
              <Field label="Note" htmlFor="day-note" className="mt-3" hint="Why a figure differs, if it does">
                <TextInput id="day-note" value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>

              <Button type="submit" busy={busy} className="mt-3">
                {phase === 'open' ? 'Open the day' : 'Close the day'}
              </Button>
            </>
          )}
        </form>
      )}

      {day.yesterdayDate && (
        <p className="mt-3 text-xs text-dpl-muted">
          Compared with {new Date(`${day.yesterdayDate}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}.
        </p>
      )}
    </section>
  )
}

function Balances({
  lines, yesterdayFor, readOnly, counted, onCount,
}: {
  lines: DayBookLine[]
  yesterdayFor: (accountId: string) => DayBookLine | undefined
  readOnly?: boolean
  counted?: Record<string, string>
  onCount?: (accountId: string, text: string) => void
}) {
  return (
    <div className="-mx-4 overflow-x-auto sm:mx-0">
      <table className="w-full min-w-[34rem] text-sm">
        <thead>
          <tr className="border-b border-dpl-border text-left text-xs uppercase tracking-wide text-dpl-muted">
            <th scope="col" className="px-2 py-2">Account</th>
            <th scope="col" className="px-2 py-2 text-right">Books say</th>
            <th scope="col" className="px-2 py-2 text-right">Counted</th>
            <th scope="col" className="px-2 py-2 text-right">Difference</th>
            <th scope="col" className="px-2 py-2 text-right">Last close</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const yesterday = yesterdayFor(line.accountId)
            const short = (line.difference ?? 0) < 0

            return (
              <tr key={line.accountId} className="border-b border-dpl-border last:border-0">
                <td className="px-2 py-1.5">
                  {line.name}
                  {/* Said on the row, because "whose is this?" decides who is answerable for it. */}
                  {line.shared && <span className="block text-xs text-dpl-muted">Company-wide, not this shop's</span>}
                </td>

                <td className="px-2 py-1.5 text-right tabular-nums">{formatMoneyDocument(line.book)}</td>

                <td className="px-2 py-1.5 text-right">
                  {readOnly || !onCount ? (
                    <span className="tabular-nums">
                      {line.declared === undefined ? <span className="text-dpl-muted">—</span> : formatMoneyDocument(line.declared)}
                    </span>
                  ) : (
                    <TextInput
                      aria-label={`${line.name} counted`}
                      inputMode="decimal"
                      value={counted?.[line.accountId] ?? ''}
                      onChange={(e) => onCount(line.accountId, e.target.value)}
                      className="w-32 text-right"
                    />
                  )}
                </td>

                <td className={`px-2 py-1.5 text-right tabular-nums ${short ? 'font-medium text-dpl-danger' : ''}`}>
                  {line.difference === undefined ? <span className="text-dpl-muted">—</span> : formatMoneyDocument(line.difference)}
                </td>

                <td className="px-2 py-1.5 text-right tabular-nums text-dpl-muted">
                  {/* Blank before the day opens: telling somebody the target before they count turns
                      the count into typing. The server withholds it; this just has nothing to show. */}
                  {yesterday?.declared === undefined ? '—' : formatMoneyDocument(yesterday.declared)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
